import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IconCpu, IconAlertTriangle, IconCheckCircle, IconKey, IconZap, IconInfo, IconBarChart, IconClipboard, IconBulb, IconSearch, IconTag } from './Icons';

const AIAnalysisPanel = ({ projectId, clusters, apiBaseUrl }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterAnalysis, setClusterAnalysis] = useState(null);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [apiNote, setApiNote] = useState(null);

  // Проверка наличия токена (для отображения ошибки)
  const checkAuth = () => {
    try {
      const authStr = localStorage.getItem('auth');
      if (authStr) {
        const authData = JSON.parse(authStr);
        return !!authData.token;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  // ИСПРАВЛЕННАЯ ФУНКЦИЯ runAIAnalysis
  const runAIAnalysis = async () => {
    // Проверяем авторизацию
    if (!checkAuth()) {
      setError('Токен авторизации отсутствует. Пожалуйста, войдите заново.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setApiNote(null);
    
    try {
      // Токен добавляется автоматически через axios interceptor из axiosConfig.js
      const response = await axios.post(
        `${apiBaseUrl}/api/project/${projectId}/ai-analyze`,
        {}
      );
      
      console.log('Ответ получен:', response.data);
      
      if (response.data.success) {
        setAnalysis(response.data.analysis);
        if (response.data.note) setApiNote(response.data.note);
        if (response.data.api_error) {
          console.log('API заметка:', response.data.api_error);
        }
      } else {
        setError(response.data.error || 'Ошибка анализа');
      }
    } catch (err) {
      console.error('Ошибка:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const analyzeCluster = async (clusterId, clusterName, examples) => {
    if (!checkAuth()) {
      alert('Токен авторизации отсутствует');
      return;
    }
    
    setClusterLoading(true);
    setSelectedCluster(clusterName);
    setError(null);
    
    try {
      // Токен добавляется автоматически через axios interceptor из axiosConfig.js
      const response = await axios.post(
        `${apiBaseUrl}/api/project/${projectId}/ai-analyze-texts`,
        {
          texts: examples.slice(0, 15),
          cluster_name: clusterName
        }
      );
      
      if (response.data.success) {
        setClusterAnalysis(response.data.analysis);
        if (response.data.note) {
          console.log('Note:', response.data.note);
        }
      } else {
        alert('Ошибка анализа: ' + (response.data.error || 'Неизвестная ошибка'));
      }
    } catch (err) {
      console.error('Cluster analysis error:', err);
      alert('Ошибка при анализе кластера: ' + (err.response?.data?.error || err.message));
    } finally {
      setClusterLoading(false);
    }
  };

  // Функция для принудительного входа
  const handleLogin = () => {
    window.location.href = '/login';
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
      case 'позитивная':
        return '#4CAF50';
      case 'negative':
      case 'негативная':
        return '#f44336';
      default:
        return '#9E9E9E';
    }
  };

  const getSentimentText = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
      case 'позитивная':
        return 'Позитивная';
      case 'negative':
      case 'негативная':
        return 'Негативная';
      default:
        return 'Нейтральная';
    }
  };

  return (
    <div className="ai-analysis-panel" style={{
      backgroundColor: 'var(--panel-2)',
      borderRadius: '12px',
      padding: '24px',
      marginTop: '24px',
      border: '1px solid var(--border)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#007bff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            color: 'white'
          }}>
            <IconCpu size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text)' }}>
              ИИ-анализ кластеров
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--muted)' }}>
              Глубокий анализ с помощью ИИ
            </p>
            {!checkAuth() && (
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#f44336', fontWeight: '500' }}>
                <IconAlertTriangle size={12} style={{ marginRight: '4px', marginTop: '-2px' }} />Токен авторизации не найден
              </p>
            )}
            {checkAuth() && (
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#4CAF50' }}>
                <IconCheckCircle size={11} style={{ marginRight: '4px', marginTop: '-2px' }} />Токен активен
              </p>
            )}
          </div>
        </div>
        
        <button
          onClick={runAIAnalysis}
          disabled={loading || !checkAuth()}
          style={{
            padding: '12px 24px',
            background: loading || !checkAuth() ? 'var(--border)' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: loading || !checkAuth() ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            opacity: loading || !checkAuth() ? 0.7 : 1
          }}
        >
          {loading ? (
            <>
              <span className="spinner" style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Анализирую...
            </>
          ) : (
            <>
              <IconZap size={16} />Запустить ИИ-анализ
            </>
          )}
        </button>
      </div>

      {!checkAuth() && (
        <div style={{
          padding: '16px',
          backgroundColor: 'rgba(255, 152, 0, 0.12)',
          border: '1px solid #ff9800',
          borderRadius: '8px',
          color: '#ffb74d',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '20px' }}><IconKey size={20} /></span>
            <div style={{ flex: 1 }}>
              <strong>Токен авторизации не найден</strong>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                Пожалуйста, <button 
                  onClick={handleLogin}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#64b5f6', 
                    textDecoration: 'underline', 
                    cursor: 'pointer',
                    fontSize: '13px',
                    padding: 0
                  }}
                >
                  войдите заново
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {apiNote && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: 'rgba(100, 181, 246, 0.15)',
          border: '1px solid #2196f3',
          borderRadius: '8px',
          color: '#9ecbff',
          marginBottom: '16px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span><IconInfo size={16} /></span>
          <span>{apiNote}</span>
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: 'rgba(244, 67, 54, 0.15)',
          border: '1px solid #f44336',
          borderRadius: '8px',
          color: '#ff8a80',
          marginBottom: '16px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span><IconAlertTriangle size={16} /></span>
          <span>{error}</span>
        </div>
      )}

      {/* Отображение анализа */}
      {analysis && analysis.clusters && (
        <div style={{ marginTop: '20px' }}>
          <div style={{
            padding: '16px',
            backgroundColor: 'rgba(100, 181, 246, 0.15)',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#90caf9', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span><IconBarChart size={15} /></span> Общий вывод
            </h4>
            <p style={{ margin: 0, color: 'var(--text)', fontSize: '14px', lineHeight: '1.5' }}>
              {analysis.overall_summary}
            </p>
          </div>

          <h4 style={{ 
            margin: '0 0 16px 0', 
            color: 'var(--text)', 
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span><IconClipboard size={16} /></span> Анализ по кластерам
          </h4>

          <div style={{ display: 'grid', gap: '16px' }}>
            {analysis.clusters.map((cluster, idx) => (
              <div
                key={idx}
                style={{
                  backgroundColor: 'var(--panel)',
                  borderRadius: '10px',
                  padding: '20px',
                  border: '1px solid var(--border)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <h5 style={{ margin: 0, color: 'var(--text)', fontSize: '16px', fontWeight: '600' }}>
                    {cluster.cluster_name}
                  </h5>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: getSentimentColor(cluster.sentiment) + '20',
                    color: getSentimentColor(cluster.sentiment),
                    border: `1px solid ${getSentimentColor(cluster.sentiment)}40`
                  }}>
                    {getSentimentText(cluster.sentiment)}
                  </span>
                </div>

                {cluster.subcategories && cluster.subcategories.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                      Подкатегории:
                    </span>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {cluster.subcategories.map((sub, i) => (
                        <span
                          key={i}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: 'var(--panel-2)',
                            borderRadius: '12px',
                            fontSize: '12px',
                            color: 'var(--muted)',
                            border: '1px solid var(--border)'
                          }}
                        >
                          {sub}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {cluster.insights && cluster.insights.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                      <IconBulb size={12} style={{ marginRight: '4px', marginTop: '-2px' }} />Ключевые инсайты:
                    </span>
                    <ul style={{
                      margin: '6px 0 0 0',
                      paddingLeft: '18px',
                      fontSize: '13px',
                      color: 'var(--text)',
                      lineHeight: '1.6'
                    }}>
                      {cluster.insights.map((insight, i) => (
                        <li key={i} style={{ marginBottom: '4px' }}>{insight}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {cluster.recommendations && cluster.recommendations.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                      <IconCheckCircle size={12} style={{ marginRight: '4px', marginTop: '-2px' }} />Рекомендации:
                    </span>
                    <ul style={{
                      margin: '6px 0 0 0',
                      paddingLeft: '18px',
                      fontSize: '13px',
                      color: '#7bd88a',
                      lineHeight: '1.6'
                    }}>
                      {cluster.recommendations.map((rec, i) => (
                        <li key={i} style={{ marginBottom: '4px' }}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const clusterData = Object.entries(clusters || {}).find(
                      ([_, data]) => data.suggested_name === cluster.cluster_name
                    );
                    if (clusterData) {
                      analyzeCluster(clusterData[0], cluster.cluster_name, clusterData[1].examples);
                    } else {
                      alert('Не удалось найти данные кластера');
                    }
                  }}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: 'var(--panel-2)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: 'var(--muted)',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span><IconSearch size={13} /></span> Детальный анализ текстов
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedCluster && clusterAnalysis && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--panel)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            width: '100%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '1px solid var(--border)'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text)' }}>
                Детальный анализ: {selectedCluster}
              </h3>
              <button
                onClick={() => {
                  setSelectedCluster(null);
                  setClusterAnalysis(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--muted)',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%'
                }}
              >
                ×
              </button>
            </div>

            {clusterLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner" style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid var(--panel-2)',
                  borderTop: '3px solid #007bff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }} />
                <p style={{ color: 'var(--muted)' }}>Анализирую тексты...</p>
              </div>
            ) : (
              <div>
                {clusterAnalysis.themes && clusterAnalysis.themes.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ 
                      fontSize: '14px', 
                      color: 'var(--muted)', 
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span><IconTag size={13} /></span> Темы:
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {clusterAnalysis.themes.map((theme, i) => (
                        <span key={i} style={{
                          padding: '6px 12px',
                          backgroundColor: 'rgba(100, 181, 246, 0.15)',
                          borderRadius: '16px',
                          fontSize: '13px',
                          color: '#90caf9',
                          border: '1px solid #bbdefb'
                        }}>
                          {theme}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {clusterAnalysis.key_points && clusterAnalysis.key_points.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ 
                      fontSize: '14px', 
                      color: 'var(--muted)', 
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span><IconBulb size={13} /></span> Ключевые моменты:
                    </h4>
                    <ul style={{ 
                      paddingLeft: '20px', 
                      margin: 0,
                      color: 'var(--text)'
                    }}>
                      {clusterAnalysis.key_points.map((point, i) => (
                        <li key={i} style={{ marginBottom: '8px', fontSize: '14px', lineHeight: '1.5' }}>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {clusterAnalysis.recommendations && clusterAnalysis.recommendations.length > 0 && (
                  <div>
                    <h4 style={{ 
                      fontSize: '14px', 
                      color: 'var(--muted)', 
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span><IconCheckCircle size={13} /></span> Рекомендации:
                    </h4>
                    <ul style={{ 
                      paddingLeft: '20px', 
                      margin: 0,
                      color: '#7bd88a'
                    }}>
                      {clusterAnalysis.recommendations.map((rec, i) => (
                        <li key={i} style={{ marginBottom: '8px', fontSize: '14px', lineHeight: '1.5' }}>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AIAnalysisPanel;